import { NavLink, Outlet } from 'react-router-dom';

import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useTheme } from '../context/ThemeContext.jsx';
import { useLiveQuery } from '../hooks/useLiveQuery.js';
import { MarketStatusPill } from './MarketStatusPill.jsx';

export function AppShell() {
  const { user, signOut } = useAuth();
  const { resolved, toggle } = useTheme();
  const { data: status } = useLiveQuery((signal) => api.marketStatus(signal), { intervalMs: 30_000 });

  return (
    <div className="shell">
      <header className="topbar">
        <NavLink to="/portfolio" className="brand">
          <span className="brand__mark" aria-hidden="true">
            H
          </span>
          Heads Up Financial
        </NavLink>

        <nav className="topbar__nav" aria-label="Main">
          <NavLink to="/portfolio" className="navlink">
            Portfolio
          </NavLink>
          {/* Between the two, not after them: orders are what this account did, which
              belongs beside the portfolio it did it to, rather than filed behind the
              board of everything it could have traded. */}
          <NavLink to="/orders" className="navlink">
            Orders
          </NavLink>
          <NavLink to="/markets" className="navlink">
            Markets
          </NavLink>
        </nav>

        <div className="topbar__meta">
          <MarketStatusPill status={status} />
          <button
            type="button"
            className="iconbutton"
            onClick={toggle}
            aria-label={`Switch to ${resolved === 'dark' ? 'light' : 'dark'} theme`}
            title={`Switch to ${resolved === 'dark' ? 'light' : 'dark'} theme`}
          >
            <span aria-hidden="true">{resolved === 'dark' ? '☀' : '☾'}</span>
          </button>

          <div className="account">
            <div>
              <div className="account__name">{user?.displayName}</div>
              <div className="account__number">{user?.accountNumber}</div>
            </div>
            <button type="button" className="button button--ghost" onClick={signOut}>
              Sign out
            </button>
          </div>
        </div>
      </header>

      <SimulationDisclaimer />

      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * The one thing on this screen a viewer could genuinely be misled by.
 *
 * The tickers and company names are the real S&P 500 constituents; every price,
 * volume and fundamental beside them is invented by a seeded simulation. `role`
 * is `note` rather than `alert` — it is permanent context, not an event, and an
 * alert would be announced on every route change.
 */
function SimulationDisclaimer() {
  return (
    <div className="disclaimer" role="note">
      <span aria-hidden="true">◆</span>
      <span>
        <strong>Simulated market.</strong> Ticker symbols, company names and sectors are the real
        S&amp;P 500 constituents; every price, volume and fundamental shown is generated by this
        demo and has never been a real quote.
      </span>
    </div>
  );
}
